/**
 * Adapter for the conversation `summary_document` endpoint.
 *
 * The endpoint's response structure changed: each `summary.sections.<key>` used
 * to be a ready-to-render markdown string, but is now a structured object/array
 * (and several section keys were renamed / added). The entire MeetingDetailsPage
 * rendering pipeline (tabs, collapsible quotes, PDF export) still consumes the
 * legacy shape — `{ summary: { sections: { <legacyKey>: markdownString } } }`.
 *
 * Rather than rewrite that pipeline, this module converts the new structured
 * response back into the legacy markdown-per-section shape so the page keeps
 * working unchanged. Old-format responses (string sections) pass through as-is.
 */

/* ----------------------------- markdown helpers ---------------------------- */

const isObj = (v: any): v is Record<string, any> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const nonEmptyArray = (v: any): v is any[] => Array.isArray(v) && v.length > 0;

// Sanitize a value for use inside a markdown table cell.
const cell = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v).replace(/\|/g, '\\|').replace(/\s*\n+\s*/g, ' ').trim();
};

// "down_payment" -> "Down Payment"
const humanize = (s: any): string =>
  String(s ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Strip a single pair of wrapping square brackets the API sometimes adds.
const strip = (s: any): string =>
  String(s ?? '').trim().replace(/^\[/, '').replace(/\]$/, '').trim();

const fmtBool = (v: any): string => {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') return humanize(v);
  return '—';
};

/* ------------------------------ section builders --------------------------- */

function buildStrategicSummary(ss: any): string {
  if (!isObj(ss)) return '';
  let md = '';
  const cn = ss.client_needs || {};
  const cnLines: string[] = [];
  if (cn.property_type) cnLines.push(`- **Property Type:** ${cn.property_type}`);
  if (cn.budget) cnLines.push(`- **Budget:** ${cn.budget}`);
  if (cn.purpose) cnLines.push(`- **Purpose:** ${cn.purpose}`);
  if (nonEmptyArray(cn.priorities)) cnLines.push(`- **Priorities:** ${cn.priorities.join(', ')}`);
  if (cnLines.length) md += `#### Client Needs\n\n${cnLines.join('\n')}\n\n`;

  if (nonEmptyArray(ss.closing_actions)) {
    md += '#### Closing Actions\n\n';
    md += ss.closing_actions.map((a: any) => `- ${a}`).join('\n') + '\n\n';
  }

  const cp = ss.channel_partner_involvement;
  if (isObj(cp) && (cp.mentioned || cp.partner_name || cp.role_context)) {
    md += '#### Channel Partner Involvement\n\n';
    md += `- **Mentioned:** ${fmtBool(cp.mentioned)}\n`;
    if (cp.partner_name) md += `- **Partner:** ${cp.partner_name}\n`;
    if (cp.role_context) md += `- **Context:** ${cp.role_context}\n`;
    md += '\n';
  }
  return md.trim();
}

function buildMeetingSummary(cq: any, tlr: any, sop: any): string {
  let md = '';

  if (isObj(cq)) {
    if (cq.call_quality_summary) md += `${cq.call_quality_summary}\n\n`;
    const scoreRows: string[] = [];
    if (cq.overall_call_quality_score != null) scoreRows.push(`| Overall Quality | ${cq.overall_call_quality_score} |`);
    if (cq.clarity_score != null) scoreRows.push(`| Clarity | ${cq.clarity_score} |`);
    if (cq.engagement_score != null) scoreRows.push(`| Engagement | ${cq.engagement_score} |`);
    if (cq.professionalism_score != null) scoreRows.push(`| Professionalism | ${cq.professionalism_score} |`);
    if (scoreRows.length) {
      md += '#### Call Quality Scores\n\n| Metric | Score |\n|---|---|\n' + scoreRows.join('\n') + '\n\n';
    }
    if (nonEmptyArray(cq.areas_for_improvement)) {
      md += '#### Areas for Improvement\n\n';
      md += cq.areas_for_improvement.map((a: any) => `- ${a}`).join('\n') + '\n\n';
    }
  }

  if (isObj(tlr)) {
    const lines: string[] = [];
    if (tlr.sales_person_talk_percentage != null) lines.push(`- **Sales Person:** ${tlr.sales_person_talk_percentage}%`);
    if (tlr.client_talk_percentage != null) lines.push(`- **Client:** ${tlr.client_talk_percentage}%`);
    if (tlr.dominant_speaker) lines.push(`- **Dominant Speaker:** ${tlr.dominant_speaker}`);
    if (lines.length || tlr.analysis) {
      md += '#### Talk-to-Listen Ratio\n\n';
      if (lines.length) md += lines.join('\n') + '\n\n';
      if (tlr.analysis) md += `${tlr.analysis}\n\n`;
    }
  }

  if (isObj(sop)) {
    md += '#### SOP Compliance\n\n';
    if (sop.overall_compliance_score != null) md += `- **Overall Compliance Score:** ${sop.overall_compliance_score}\n`;
    if (sop.overall_deviation_percentage != null) md += `- **Deviation:** ${sop.overall_deviation_percentage}%\n`;
    md += '\n';
    const cs = sop.criteria_scores;
    if (isObj(cs)) {
      const rows = Object.keys(cs).map((key) => {
        const c = cs[key] || {};
        return `| ${humanize(key)} | ${cell(c.timing)} | ${fmtBool(c.completed)} | ${cell(c.quality_score)} |`;
      });
      if (rows.length) {
        md += '| Criteria | Timing | Completed | Quality |\n|---|---|---|---|\n' + rows.join('\n') + '\n\n';
      }
    }
  }
  return md.trim();
}

function buildActionItems(ai: any): string {
  if (!isObj(ai)) return '';
  const renderGroup = (title: string, arr: any): string => {
    if (!nonEmptyArray(arr)) return '';
    let g = `#### ${title}\n\n`;
    g += arr
      .map((item: any) => {
        const meta: string[] = [];
        if (item.status) meta.push(`status: ${item.status}`);
        if (item.priority) meta.push(`priority: ${item.priority}`);
        if (item.deadline) meta.push(`deadline: ${item.deadline}`);
        const suffix = meta.length ? ` _(${meta.join(', ')})_` : '';
        return `- ${item.text || ''}${suffix}`;
      })
      .join('\n');
    return g + '\n\n';
  };
  return (renderGroup('Client', ai.client) + renderGroup('Sales Person', ai.sales_person)).trim();
}

function buildParticipants(cs: any): string {
  if (!isObj(cs) || !isObj(cs.participants_and_roles)) return '';
  const pr = cs.participants_and_roles;
  let md = '';
  if (pr.salesperson_rm) md += `- **Salesperson / RM:** ${pr.salesperson_rm}\n\n`;
  if (nonEmptyArray(pr.client_speakers)) {
    md += '#### Client Speakers\n\n';
    md += pr.client_speakers
      .map((sp: any) => `- **${sp.label || 'Client'}:** ${sp.description || ''}`)
      .join('\n');
  }
  return md.trim();
}

function buildQuestions(tq: any): string {
  if (!nonEmptyArray(tq)) return '';
  return tq
    .map((q: any) => {
      const cat = q.category ? `**[${q.category}]** ` : '';
      const ts = q.timestamp ? ` *${q.timestamp}*` : '';
      return `- ${cat}${q.question_text || ''}${ts}`;
    })
    .join('\n')
    .trim();
}

function buildClientSnapshot(cs: any): string {
  if (!isObj(cs)) return '';
  let md = '';

  const prof = cs.profile || {};
  const profileLines: string[] = [];
  if (cs.name?.text) profileLines.push(`- **Name:** ${cs.name.text}`);
  if (prof.residence) profileLines.push(`- **Residence:** ${prof.residence}`);
  if (prof.occupation) profileLines.push(`- **Occupation:** ${prof.occupation}`);
  if (prof.age) profileLines.push(`- **Age:** ${prof.age}`);
  if (prof.family) profileLines.push(`- **Family:** ${prof.family}`);
  if (profileLines.length) md += `#### Profile\n\n${profileLines.join('\n')}\n\n`;

  if (cs.budget) md += `#### Budget\n\n${cs.budget}\n\n`;

  const intent = cs.intent || {};
  const intentLines: string[] = [];
  if (intent.purpose) intentLines.push(`- **Purpose:** ${intent.purpose}`);
  if (nonEmptyArray(intent.projects)) intentLines.push(`- **Projects:** ${intent.projects.join('; ')}`);
  if (intentLines.length) md += `#### Intent\n\n${intentLines.join('\n')}\n\n`;

  const tl = cs.timeline || {};
  const tlLines: string[] = [];
  if (tl.decision) tlLines.push(`- **Decision:** ${tl.decision}`);
  if (tl.possession) tlLines.push(`- **Possession:** ${tl.possession}`);
  if (nonEmptyArray(tl.next_actions)) tl.next_actions.forEach((a: any) => tlLines.push(`- **Next Action:** ${a}`));
  if (tlLines.length) md += `#### Timeline\n\n${tlLines.join('\n')}\n\n`;

  if (nonEmptyArray(cs.decision_makers)) {
    md += '#### Decision Makers\n\n' + cs.decision_makers.map((d: any) => `- ${d}`).join('\n') + '\n\n';
  }

  const ltb = cs.likelihood_to_buy || {};
  if (ltb.percentage != null || ltb.reasoning) {
    md += '#### Likelihood to Buy\n\n';
    if (ltb.percentage != null) md += `- **Likelihood:** ${ltb.percentage}%\n`;
    if (ltb.reasoning) md += `- ${ltb.reasoning}\n`;
    md += '\n';
  }

  const bic = cs.buyer_intent_classification || {};
  if (bic.type || bic.luxury_preference_score != null) {
    md += '#### Buyer Intent\n\n';
    if (bic.type) md += `- **Type:** ${bic.type}\n`;
    if (bic.luxury_preference_score != null) md += `- **Luxury Preference Score:** ${bic.luxury_preference_score}\n`;
    md += '\n';
  }

  if (nonEmptyArray(cs.amenities_discussed)) {
    md += '#### Amenities Discussed\n\n| Amenity | Sentiment |\n|---|---|\n';
    md += cs.amenities_discussed.map((a: any) => `| ${cell(a.amenity)} | ${cell(a.sentiment)} |`).join('\n') + '\n\n';
  }

  if (nonEmptyArray(cs.competitors_mentioned)) {
    md += '#### Competitors Mentioned\n\n' + cs.competitors_mentioned.map((c: any) => `- ${c}`).join('\n') + '\n\n';
  }

  return md.trim();
}

function buildDriversRisk(drivers: any, objections: any): string {
  let md = '';
  if (nonEmptyArray(drivers)) {
    drivers.forEach((d: any) => {
      md += `### ${d.label || 'Driver'}\n\n`;
      if (d.priority) md += `- **Priority:** ${d.priority}\n`;
      if (d.need) md += `- **Need:** ${d.need}\n`;
      if (d.pain_point) md += `- **Pain Point:** ${d.pain_point}\n`;
      if (d.action) md += `- **Recommended Action:** ${d.action}\n`;
      md += '\n';
      if (nonEmptyArray(d.supporting_quotes)) {
        // The "Supporting Quotes" header is detected by the page's quote parser,
        // which turns the following block into a collapsible panel.
        md += 'Supporting Quotes\n\n';
        d.supporting_quotes.forEach((q: any) => {
          const ts = q.timestamp ? ` ${q.timestamp}` : '';
          md += `> ${q.text || ''}${ts}\n\n`;
        });
      }
    });
  }

  if (nonEmptyArray(objections)) {
    md += '### Key Objections\n\n';
    md += objections
      .map((o: any) => {
        const sev = o.severity ? `**[${o.severity}]** ` : '';
        const cat = o.objection_category ? ` _(${o.objection_category})_` : '';
        const ts = o.timestamp ? ` *${o.timestamp}*` : '';
        return `- ${sev}${o.objection_text || ''}${cat}${ts}`;
      })
      .join('\n') + '\n\n';
  }
  return md.trim();
}

function buildProductOptions(po: any): string {
  if (!isObj(po)) return '';
  const renderTable = (title: string, t: any): string => {
    if (!isObj(t) || !nonEmptyArray(t.rows)) return '';
    const options: any[] = Array.isArray(t.options) ? t.options : [];
    let g = `#### ${title}\n\n`;
    g += '| Feature | ' + options.map((o) => cell(o)).join(' | ') + ' |\n';
    g += '|---|' + options.map(() => '---').join('|') + '|\n';
    g += t.rows
      .map((r: any) => {
        const vals = (Array.isArray(r.values) ? r.values : []).map((v: any) => cell(v));
        while (vals.length < options.length) vals.push('—');
        return `| ${cell(humanize(r.feature))} | ${vals.join(' | ')} |`;
      })
      .join('\n');
    return g + '\n\n';
  };
  return (
    renderTable('Property Details', po.property_details) +
    renderTable('Payment Plans', po.payment_plans) +
    renderTable('Features & Benefits', po.features_and_benefits) +
    renderTable('Competitive Analysis', po.competitive_analysis)
  ).trim();
}

function buildEmotionalArc(ea: any, ds: any): string {
  let md = '';
  if (isObj(ea)) {
    const lines: string[] = [];
    if (ea.trajectory) lines.push(`- **Trajectory:** ${humanize(ea.trajectory)}`);
    if (ea.start_sentiment_score != null) lines.push(`- **Start Sentiment:** ${ea.start_sentiment_score}`);
    if (ea.end_sentiment_score != null) lines.push(`- **End Sentiment:** ${ea.end_sentiment_score}`);
    if (lines.length) md += lines.join('\n') + '\n\n';

    const prog = ea.progression;
    if (isObj(prog)) {
      const pLines: string[] = [];
      if (prog.start) pLines.push(`- **Start:** ${strip(prog.start)}`);
      if (prog.middle) pLines.push(`- **Middle:** ${strip(prog.middle)}`);
      if (prog.end) pLines.push(`- **End:** ${strip(prog.end)}`);
      if (pLines.length) md += `#### Progression\n\n${pLines.join('\n')}\n\n`;
    }

    const ot = ea.overall_takeaway;
    if (isObj(ot)) {
      if (nonEmptyArray(ot.key_drivers)) {
        md += '#### Key Drivers\n\n' + ot.key_drivers.map((d: any) => `- ${strip(d)}`).join('\n') + '\n\n';
      }
      if (nonEmptyArray(ot.main_hurdles)) {
        md += '#### Main Hurdles\n\n' + ot.main_hurdles.map((h: any) => `- ${strip(h)}`).join('\n') + '\n\n';
      }
    }
  }

  if (isObj(ds)) {
    md += '#### Deal Sentiment\n\n';
    if (ds.overall_deal_sentiment_text) md += `${ds.overall_deal_sentiment_text}\n\n`;
    const lines: string[] = [];
    if (ds.overall_deal_sentiment_score != null) lines.push(`- **Overall Sentiment Score:** ${ds.overall_deal_sentiment_score}`);
    if (ds.buyer_interest_level) {
      const score = ds.buyer_interest_level_score != null ? ` (${ds.buyer_interest_level_score})` : '';
      lines.push(`- **Buyer Interest:** ${ds.buyer_interest_level}${score}`);
    }
    if (ds.decision_readiness_score != null) lines.push(`- **Decision Readiness:** ${ds.decision_readiness_score}`);
    if (ds.deal_value != null) lines.push(`- **Deal Value:** ${ds.deal_value}`);
    if (lines.length) md += lines.join('\n') + '\n\n';
    if (ds.decision_readiness_summary) md += `${ds.decision_readiness_summary}\n\n`;

    if (nonEmptyArray(ds.positive_sentiment_drivers)) {
      md += '#### Positive Sentiment Drivers\n\n' + ds.positive_sentiment_drivers.map((d: any) => `- ${d}`).join('\n') + '\n\n';
    }
    if (nonEmptyArray(ds.negative_sentiment_drivers)) {
      md += '#### Negative Sentiment Drivers\n\n' + ds.negative_sentiment_drivers.map((d: any) => `- ${d}`).join('\n') + '\n\n';
    }
    if (nonEmptyArray(ds.key_points_of_interest)) {
      md += '#### Key Points of Interest\n\n' + ds.key_points_of_interest.map((d: any) => `- ${d}`).join('\n') + '\n\n';
    }
  }
  return md.trim();
}

/* -------------------------------- public API ------------------------------- */

/**
 * Returns true when the response uses the new structured format (section values
 * are objects/arrays) rather than the legacy markdown-string format.
 */
export function isStructuredSummaryDocument(resp: any): boolean {
  const sections = resp?.summary?.sections;
  if (!isObj(sections)) return false;
  return Object.values(sections).some((v) => v !== null && typeof v === 'object');
}

/**
 * Convert the new structured summary_document response into the legacy
 * `{ summary: { sections: { <legacyKey>: markdown } } }` shape. Old-format
 * responses (and anything unrecognized) are returned unchanged.
 */
export function transformSummaryDocument(resp: any): any {
  if (!isStructuredSummaryDocument(resp)) return resp;

  const s = resp.summary.sections || {};
  const sections: Record<string, string> = {};

  const assign = (key: string, value: string) => {
    if (value && value.trim()) sections[key] = value;
  };

  assign('strategic_summary', buildStrategicSummary(s.strategic_summary));
  assign('meeting_summary', buildMeetingSummary(s.call_quality_score, s.talk_listen_ratio, s.sop_deviation));
  assign('action_items', buildActionItems(s.action_items));
  assign('participants', buildParticipants(s.client_snapshot));
  assign('questions_raised', buildQuestions(s.top_questions));
  assign('client_snapshot', buildClientSnapshot(s.client_snapshot));
  assign('key_drivers_risk', buildDriversRisk(s.key_drivers_risk || s.drivers_risk, s.top_objections));
  assign('product_options', buildProductOptions(s.product_options));
  assign('emotional_arc', buildEmotionalArc(s.emotional_arc, s.deal_sentiment));

  return {
    ...resp,
    summary: {
      ...resp.summary,
      sections,
    },
  };
}
